// Taproom Orders — Sales & Distribution > Taproom (mobile-first step flow)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { isValidKegCode, normalizeKegCode } from '../utils/kegCode';
import {
  availableForItem,
  capQty,
  isKegPackage,
  type OnHandItem,
} from '../utils/transferItems';
import KegScanOverlay from './KegScanOverlay';
import { primeScanAudio } from '../utils/scanFeedback';
import {
  assignScannedKegToLines,
  kegScanGroups,
  removeKegFromLines,
} from '../utils/taproomKegScan';
import { sortTaproomOrderableItems } from '../utils/taproomOrderItems';

interface TaproomSite {
  key: string;
  label: string;
  brewerySiteId: string;
  taproomSiteId: string;
  customerName: string;
}

interface TaproomLine {
  id?: number;
  itemName: string;
  quantity: number;
  unit?: string;
  kegCodes?: string[];
  productId?: number | null;
  productName?: string | null;
  packageType?: string | null;
}

interface TaproomOrder {
  orderId: number;
  status: string;
  createdDate: string;
  orderDate?: string;
  periodWarnings?: string[];
  siteId?: string;
  siteKey?: string | null;
  siteLabel?: string | null;
  customerName?: string;
  toNumber?: string | null;
  invoiceId?: number | string | null;
  invoiceNumber?: string | null;
  transferId?: number | string | null;
  transferNumber?: string | null;
  fromLocationId?: number | null;
  toLocationId?: number | null;
  fromLocationName?: string | null;
  toLocationName?: string | null;
  items?: TaproomLine[];
  itemCount?: number;
}

type FlowStep = 1 | 2 | 3 | 4;

function useIsNarrow() {
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 767
  );
  useEffect(() => {
    const apply = () => setIsNarrow(window.innerWidth <= 767);
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);
  return isNarrow;
}

export const TaproomOrdersList: React.FC = () => {
  const navigate = useNavigate();
  const isNarrow = useIsNarrow();
  const [orders, setOrders] = useState<TaproomOrder[]>([]);
  const [sites, setSites] = useState<TaproomSite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    const [listRes, sitesRes] = await Promise.all([
      apiFetch(`${API_BASE_URL}/api/taproom-orders`, { headers: { Accept: 'application/json' } }),
      apiFetch(`${API_BASE_URL}/api/taproom-orders/sites`, {
        headers: { Accept: 'application/json' },
      }),
    ]);
    if (!listRes.ok) throw new Error(`HTTP ${listRes.status}`);
    const data = await listRes.json();
    setOrders(data.orders || []);
    if (sitesRes.ok) {
      const s = await sitesRes.json();
      setSites(s.sites || []);
    }
  }, []);

  useEffect(() => {
    load().catch((err: Error) => setError(err.message));
  }, [load]);

  const deleteDraft = async (order: TaproomOrder) => {
    if (String(order.status) !== 'Draft') return;
    const label = order.toNumber ? `TO ${order.toNumber}` : `Order #${order.orderId}`;
    const ok = window.confirm(
      `Delete draft ${label}?\n\nScanned kegs on this draft will be released. This cannot be undone.`
    );
    if (!ok) return;
    setDeletingId(order.orderId);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/taproom-orders/${order.orderId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete draft');
    } finally {
      setDeletingId(null);
    }
  };

  const startOrder = async (siteKey: string) => {
    setCreating(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/taproom-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ siteKey }),
      });
      if (!res.ok) throw new Error(await res.text());
      const created = await res.json();
      navigate(`/taproom-orders/${created.orderId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create order');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="page-container container flow-page"
      data-testid="taproom-orders-list"
      data-narrow={isNarrow ? 'true' : 'false'}
    >
      <Link to="/" className="flow-back">
        ← Home
      </Link>
      <h2 className="app-header">Taproom Orders</h2>
      <p className="flow-hint">
        Internal OBB / OBBA taproom moves from the brewery cooler. No pricing — inventory moves on
        complete with a TO # and BOL.
      </p>
      {error && <div className="error">{error}</div>}

      <div className="flow-card" style={{ marginBottom: 16 }}>
        <h3 className="flow-section-title">New order</h3>
        <div className="flow-card-list">
          {sites.map((s) => (
            <button
              key={s.key}
              type="button"
              className="flow-card tappable"
              disabled={creating}
              data-testid={`taproom-new-${s.key}`}
              onClick={() => void startOrder(s.key)}
            >
              <p className="flow-card-title">{s.label}</p>
              <p className="flow-card-meta">Customer: {s.customerName}</p>
            </button>
          ))}
          {!sites.length && <p className="flow-hint">Loading sites…</p>}
        </div>
      </div>

      <h3 className="flow-section-title">Recent</h3>
      <div className="flow-mobile-cards flow-card-list">
        {orders.map((o) => (
          <div key={o.orderId} className="flow-card" data-testid={`taproom-card-${o.orderId}`}>
            <button
              type="button"
              className="flow-card tappable"
              style={{ boxShadow: 'none', border: 'none', padding: 0, width: '100%' }}
              onClick={() => navigate(`/taproom-orders/${o.orderId}`)}
            >
              <div className="flow-card-row">
                <p className="flow-card-title" style={{ margin: 0 }}>
                  {o.toNumber ? `TO ${o.toNumber}` : `Order #${o.orderId}`}
                </p>
                <span className={`flow-badge ${String(o.status).toLowerCase()}`}>{o.status}</span>
              </div>
              <p className="flow-card-meta">
                {o.customerName} · {o.createdDate} · {o.itemCount ?? 0} items
              </p>
            </button>
            {String(o.status) === 'Draft' && (
              <button
                type="button"
                className="btn-danger"
                data-testid={`taproom-delete-${o.orderId}`}
                style={{ minHeight: 44, width: '100%', marginTop: 10 }}
                disabled={deletingId === o.orderId}
                onClick={(e) => {
                  e.stopPropagation();
                  void deleteDraft(o);
                }}
              >
                {deletingId === o.orderId ? 'Deleting…' : 'Delete'}
              </button>
            )}
          </div>
        ))}
        {!orders.length && <p className="flow-hint">No taproom orders yet.</p>}
      </div>

      <table className="inventory-table flow-desktop-table" data-testid="taproom-orders-table">
        <thead>
          <tr>
            <th>TO #</th>
            <th>Order</th>
            <th>Customer</th>
            <th>Date</th>
            <th>Status</th>
            <th>Items</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.orderId}>
              <td>{o.toNumber || '—'}</td>
              <td>#{o.orderId}</td>
              <td>{o.customerName}</td>
              <td>{o.createdDate}</td>
              <td>{o.status}</td>
              <td>{o.itemCount ?? 0}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button type="button" onClick={() => navigate(`/taproom-orders/${o.orderId}`)}>
                  View
                </button>
                {String(o.status) === 'Draft' && (
                  <button
                    type="button"
                    className="btn-danger"
                    data-testid={`taproom-table-delete-${o.orderId}`}
                    style={{ marginLeft: 8, minHeight: 36 }}
                    disabled={deletingId === o.orderId}
                    onClick={() => void deleteDraft(o)}
                  >
                    {deletingId === o.orderId ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const TaproomOrderDetail: React.FC = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const isNarrow = useIsNarrow();
  const [order, setOrder] = useState<TaproomOrder | null>(null);
  const [onHandItems, setOnHandItems] = useState<OnHandItem[]>([]);
  const [lines, setLines] = useState<TaproomLine[]>([]);
  const [step, setStep] = useState<FlowStep>(1);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [itemQuery, setItemQuery] = useState('');
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sites, setSites] = useState<TaproomSite[]>([]);

  const editable = !!order && order.status === 'Draft';

  const load = useCallback(async () => {
    const res = await apiFetch(`${API_BASE_URL}/api/taproom-orders/${orderId}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    setOrder(data);
    setLines(
      (data.items || []).map((i: TaproomLine) => ({
        ...i,
        kegCodes: i.kegCodes || [],
      }))
    );
  }, [orderId]);

  useEffect(() => {
    load().catch((err: Error) => setError(err.message));
    apiFetch(`${API_BASE_URL}/api/taproom-orders/sites`, { headers: { Accept: 'application/json' } })
      .then(async (r) => {
        if (r.ok) setSites((await r.json()).sites || []);
      })
      .catch(() => undefined);
  }, [load]);

  useEffect(() => {
    const locId = order?.fromLocationId;
    if (!locId) {
      setOnHandItems([]);
      return;
    }
    let cancelled = false;
    apiFetch(`${API_BASE_URL}/api/locations/${locId}/on-hand`, {
      headers: { Accept: 'application/json' },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setOnHandItems(
          (data.items || []).filter((i: OnHandItem) => Number(i.quantity) > 0) as OnHandItem[]
        );
      })
      .catch(() => {
        if (!cancelled) setOnHandItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [order?.fromLocationId]);

  const filteredOnHand = useMemo(() => {
    const q = itemQuery.trim().toLowerCase();
    const rows = q
      ? onHandItems.filter((i) => i.itemName.toLowerCase().includes(q))
      : onHandItems;
    return sortTaproomOrderableItems(rows);
  }, [onHandItems, itemQuery]);

  const kegLines = useMemo(() => lines.filter(isKegPackage), [lines]);
  const kegScanIncomplete = useMemo(
    () =>
      kegLines.some((l) => (l.kegCodes || []).length !== Math.round(Number(l.quantity) || 0)),
    [kegLines]
  );
  const totalKegNeed = useMemo(
    () => kegLines.reduce((s, l) => s + Math.round(Number(l.quantity) || 0), 0),
    [kegLines]
  );
  const totalKegHave = useMemo(
    () => kegLines.reduce((s, l) => s + (l.kegCodes || []).length, 0),
    [kegLines]
  );
  const needsScan = kegLines.length > 0;
  const step2Ok = lines.some((l) => l.itemName && Number(l.quantity) > 0);

  const saveLines = async (next: TaproomLine[]) => {
    if (!order || !editable) return;
    setSaving(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/taproom-orders/${order.orderId}/lines`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          lines: next.map((l) => ({
            itemName: l.itemName,
            qty: l.quantity,
            unit: l.unit,
            kegCodes: l.kegCodes || [],
          })),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setOrder(data);
      setLines(
        (data.items || []).map((i: TaproomLine) => ({
          ...i,
          kegCodes: i.kegCodes || [],
        }))
      );
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const changeSite = async (siteKey: string) => {
    if (!order || !editable) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/taproom-orders/${order.orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ siteKey }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setOrder(data);
      setLines([]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const changeOrderDate = async (orderDate: string) => {
    if (!order || !editable) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/taproom-orders/${order.orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ orderDate }),
        allowHttpError: true,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setOrder(body);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const orderDateValue = order?.orderDate || order?.createdDate || '';
  const periodWarnings = order?.periodWarnings || [];

  const setLineQty = (itemName: string, qty: number, unit?: string) => {
    const max = availableForItem(onHandItems, itemName);
    const capped = capQty(qty, max);
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.itemName === itemName);
      if (capped <= 0) return prev.filter((l) => l.itemName !== itemName);
      if (idx >= 0) {
        const next = [...prev];
        const keep = Math.round(capped);
        next[idx] = {
          ...next[idx],
          quantity: capped,
          unit: unit || next[idx].unit || 'ea',
          kegCodes: (next[idx].kegCodes || []).slice(0, keep),
        };
        return next;
      }
      return [...prev, { itemName, quantity: capped, unit: unit || 'ea', kegCodes: [] }];
    });
  };

  const submitScannedKeg = async (codeRaw: string) => {
    if (!order) throw new Error('Order not loaded');
    const code = normalizeKegCode(codeRaw);
    if (!isValidKegCode(code)) throw new Error('Valid keg tag needed (e.g., EK09503)');

    const res = await apiFetch(`${API_BASE_URL}/api/kegs/${encodeURIComponent(code)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Keg ${code} not found`);
    const keg = await res.json();

    const result = assignScannedKegToLines(code, keg, lines, {
      fromLocationId: order.fromLocationId,
    });
    setLines(result.lines);
    setError(null);
  };

  const closeScanModal = () => {
    setScanModalOpen(false);
  };

  const deleteDraft = async () => {
    if (!order || order.status !== 'Draft') return;
    const label = order.toNumber ? `TO ${order.toNumber}` : `Order #${order.orderId}`;
    const ok = window.confirm(
      `Delete draft ${label}?\n\nScanned kegs on this draft will be released. This cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/taproom-orders/${order.orderId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      navigate('/taproom-orders');
    } catch (err: any) {
      setError(err.message || 'Failed to delete draft');
    } finally {
      setDeleting(false);
    }
  };

  const complete = async () => {
    if (!order) return;
    try {
      setError(null);
      if (kegScanIncomplete) {
        setError('Scan all keg serials before completing');
        setStep(3);
        return;
      }
      if (editable) await saveLines(lines);
      setSaving(true);
      const res = await apiFetch(`${API_BASE_URL}/api/taproom-orders/${order.orderId}/complete`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(await res.text());
      setOrder(await res.json());
      setStep(4);
    } catch (err: any) {
      setError('Complete failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    setError(null);
    if (step === 1) {
      if (!order?.fromLocationId) {
        setError('Pick a taproom site');
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!step2Ok) {
        setError('Add at least one item');
        return;
      }
      try {
        await saveLines(lines);
      } catch {
        return;
      }
      setStep(needsScan ? 3 : 4);
      return;
    }
    if (step === 3) {
      if (kegScanIncomplete) {
        setError('Scan all required kegs');
        return;
      }
      try {
        await saveLines(lines);
      } catch {
        return;
      }
      setStep(4);
    }
  };

  const goBack = () => {
    if (step === 1) {
      navigate('/taproom-orders');
      return;
    }
    if (step === 4 && needsScan) setStep(3);
    else if (step === 4) setStep(2);
    else if (step === 3) setStep(2);
    else setStep(1);
  };

  if (!order) {
    return (
      <div className="page-container container flow-page">
        <p>{error || 'Loading…'}</p>
      </div>
    );
  }

  const selectedMap = new Map(lines.map((l) => [l.itemName, l]));

  return (
    <div
      className="page-container container flow-page"
      data-testid="taproom-order-detail"
      data-narrow={isNarrow ? 'true' : 'false'}
    >
      <Link to="/taproom-orders" className="flow-back">
        ← Taproom Orders
      </Link>
      <h2 className="app-header">
        {order.toNumber ? `TO ${order.toNumber}` : `Order #${order.orderId}`}
      </h2>
      <p className="flow-hint">{order.siteLabel || order.customerName}</p>
      {error && <div className="error">{error}</div>}
      {periodWarnings.length > 0 && (
        <div className="error" role="status" data-testid="taproom-period-warnings" style={{ background: '#fff4e0', color: '#8a5300', border: '1px solid #f0c674' }}>
          {periodWarnings.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </div>
      )}

      {editable && (
        <div className="flow-steps">
          {(
            [
              { n: 1 as FlowStep, label: '1 Site' },
              { n: 2 as FlowStep, label: '2 Items' },
              ...(needsScan || step === 3
                ? [{ n: 3 as FlowStep, label: '3 Scan' }]
                : []),
              { n: 4 as FlowStep, label: needsScan ? '4 Review' : '3 Review' },
            ] as { n: FlowStep; label: string }[]
          ).map(({ n, label }) => (
            <button
              key={n}
              type="button"
              className={`flow-step ${step === n ? 'active' : ''}`}
              onClick={() => setStep(n)}
              data-testid={`taproom-step-${n}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {step === 1 && editable && (
        <div data-testid="taproom-step-site">
          <h3 className="flow-section-title">Taproom site</h3>
          <div className="flow-card-list">
            {sites.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`flow-card tappable ${order.siteKey === s.key ? 'selected' : ''}`}
                onClick={() => void changeSite(s.key)}
              >
                <p className="flow-card-title">{s.label}</p>
                <p className="flow-card-meta">{s.customerName}</p>
              </button>
            ))}
          </div>
          <div className="flow-card" style={{ marginTop: 12 }}>
            <p className="flow-card-meta">
              Cooler: {order.fromLocationName || '—'} → {order.toLocationName || '—'}
            </p>
            <div className="flow-field" style={{ marginTop: 12 }}>
              <label htmlFor="tpo-order-date">Order date</label>
              <input
                id="tpo-order-date"
                type="date"
                data-testid="taproom-order-date"
                value={orderDateValue}
                max={new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' })}
                disabled={saving}
                onChange={(e) => void changeOrderDate(e.target.value)}
                style={{ minHeight: 48, fontSize: 16, width: '100%', maxWidth: 280 }}
              />
              <p className="flow-hint" style={{ marginBottom: 0 }}>
                Defaults to today. Use an earlier date if the move already happened — transfer, invoice,
                ledger, keg history, and ABC month all use this date. Future dates are not allowed.
              </p>
            </div>
            <p className="flow-hint" style={{ marginBottom: 0 }}>
              No pricing — this is an internal taproom move.
            </p>
          </div>
        </div>
      )}

      {step === 2 && editable && (
        <div data-testid="taproom-step-items">
          <h3 className="flow-section-title">Add items from brewery cooler</h3>
          <div className="flow-field">
            <label htmlFor="tpo-search">Search on-hand</label>
            <input
              id="tpo-search"
              value={itemQuery}
              onChange={(e) => setItemQuery(e.target.value)}
              placeholder="Filter items…"
            />
          </div>
          {lines.length > 0 && (
            <>
              <p className="flow-hint">Selected ({lines.length})</p>
              <div className="flow-card-list" style={{ marginBottom: 16 }}>
                {lines.map((line) => {
                  const avail = availableForItem(onHandItems, line.itemName);
                  const unit =
                    onHandItems.find((i) => i.itemName === line.itemName)?.unit ||
                    line.unit ||
                    'ea';
                  return (
                    <div key={line.itemName} className="flow-card selected">
                      <p className="flow-card-title">{line.itemName}</p>
                      <p className="flow-card-meta">
                        Available {avail} {unit}
                      </p>
                      <div className="flow-card-row">
                        <div className="flow-stepper">
                          <button
                            type="button"
                            aria-label="Decrease"
                            onClick={() => setLineQty(line.itemName, Number(line.quantity) - 1, unit)}
                          >
                            −
                          </button>
                          <span className="flow-stepper-value">{line.quantity}</span>
                          <button
                            type="button"
                            aria-label="Increase"
                            onClick={() => setLineQty(line.itemName, Number(line.quantity) + 1, unit)}
                          >
                            +
                          </button>
                        </div>
                        <button
                          type="button"
                          style={{ minHeight: 44, background: '#ffe6e6', color: '#a00' }}
                          onClick={() => setLineQty(line.itemName, 0)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <div className="flow-card-list">
            {filteredOnHand.map((item) => {
              const selected = selectedMap.get(item.itemName);
              return (
                <button
                  key={item.itemName}
                  type="button"
                  data-testid="taproom-on-hand-item"
                  className={`flow-card tappable ${selected ? 'selected' : ''}`}
                  onClick={() => {
                    if (!selected) setLineQty(item.itemName, 1, item.unit);
                  }}
                >
                  <p className="flow-card-title">{item.itemName}</p>
                  <div className="flow-card-row">
                    <span className="flow-card-meta">
                      {item.quantity} {item.unit || 'ea'} available
                    </span>
                    {selected ? (
                      <span className="flow-badge complete">Added · {selected.quantity}</span>
                    ) : (
                      <span className="flow-badge">Tap to add</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 3 && editable && (
        <div data-testid="taproom-step-scan">
          <h3 className="flow-section-title">Scan kegs</h3>
          <p
            className={`flow-scan-count ${totalKegHave === totalKegNeed ? 'complete' : ''}`}
            data-testid="taproom-keg-total-progress"
          >
            {totalKegHave} of {totalKegNeed} scanned
          </p>
          <div className="flow-card-list" style={{ marginBottom: 16 }}>
            {kegScanGroups(lines).map((g) => (
              <div key={g.itemName} className="flow-card" data-testid={`taproom-scan-summary-${g.itemName}`}>
                <p className="flow-card-title">{g.styleLabel}</p>
                <p
                  className={`flow-scan-count ${g.complete ? 'complete' : ''}`}
                  style={{ fontSize: 18, textAlign: 'left' }}
                >
                  {g.scanned} of {g.ordered} scanned
                </p>
                {g.codes.length > 0 && (
                  <p className="flow-card-meta">Kegs: {g.codes.join(', ')}</p>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn-primary"
            style={{ minHeight: 52, width: '100%' }}
            data-testid="taproom-open-scan-modal"
            onClick={() => {
              primeScanAudio(); // iOS: sound needs this tap
              setScanModalOpen(true);
            }}
          >
            Scan Kegs
          </button>
        </div>
      )}

      {scanModalOpen && editable && (
        <KegScanOverlay
          title="Scan Kegs"
          subtitle={order ? `${order.toNumber ? `TO ${order.toNumber} · ` : ''}${order.toLocationName || ''}` : undefined}
          codes={lines.flatMap((l) => (l.kegCodes || []).map((c) => normalizeKegCode(c)))}
          target={totalKegNeed}
          lines={kegScanGroups(lines).map((g) => ({
            key: g.itemName,
            label: g.styleLabel,
            needed: g.ordered,
            scanned: g.scanned,
          }))}
          describe={(c) => lines.find((l) => (l.kegCodes || []).map((x) => normalizeKegCode(x)).includes(c))?.itemName}
          onScan={(code) => submitScannedKeg(code)}
          onRemove={(c) => {
            setLines((prev) => removeKegFromLines(prev, c));
          }}
          onDone={closeScanModal}
        />
      )}

      {(step === 4 || !editable) && (
        <div data-testid="taproom-step-review">
          <h3 className="flow-section-title">
            {editable ? 'Review & complete' : 'Order review'}
          </h3>
          <div className="flow-card" style={{ marginBottom: 12 }}>
            {!editable && (
              <p className="flow-card-meta" data-testid="taproom-review-status">
                <strong>Status:</strong> {order.status}
              </p>
            )}
            <p className="flow-card-meta">
              <strong>Site:</strong> {order.siteLabel || order.customerName}
            </p>
            <p className="flow-card-meta">
              <strong>From:</strong> {order.fromLocationName}
            </p>
            <p className="flow-card-meta">
              <strong>To:</strong> {order.toLocationName}
            </p>
            {editable ? (
              <div className="flow-field" style={{ marginTop: 8 }}>
                <label htmlFor="tpo-order-date-review">Order date</label>
                <input
                  id="tpo-order-date-review"
                  type="date"
                  data-testid="taproom-order-date-review"
                  value={orderDateValue}
                  max={new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' })}
                  disabled={saving}
                  onChange={(e) => void changeOrderDate(e.target.value)}
                  style={{ minHeight: 48, fontSize: 16, width: '100%', maxWidth: 280 }}
                />
              </div>
            ) : (
              <p className="flow-card-meta" data-testid="taproom-review-order-date">
                <strong>Order date:</strong> {orderDateValue}
              </p>
            )}
            <p className="flow-card-meta">
              <strong>TO #:</strong> {order.toNumber || '(assigned on complete)'}
            </p>
            {(order.invoiceNumber || order.invoiceId) && (
              <p className="flow-card-meta" data-testid="taproom-review-invoice">
                <strong>Invoice #:</strong> {order.invoiceNumber || order.invoiceId}
              </p>
            )}
            {(order.transferNumber || order.transferId) && (
              <p className="flow-card-meta" data-testid="taproom-review-transfer">
                <strong>Transfer #:</strong> {order.transferNumber || order.transferId}
              </p>
            )}
            <p className="flow-hint">Price: $0 · Tax Free · Not shown in distributor sales</p>
          </div>
          <div className="flow-card-list" data-testid="taproom-review-lines">
            {lines.map((line, idx) => {
              const codes = line.kegCodes || [];
              return (
                <div key={idx} className="flow-card" data-testid="taproom-review-line">
                  <p className="flow-card-title">{line.itemName}</p>
                  <p className="flow-card-meta">
                    Qty {line.quantity} {line.unit || 'ea'}
                  </p>
                  {codes.length > 0 && (
                    <p className="flow-card-meta" data-testid="taproom-review-kegs">
                      Kegs: {codes.join(', ')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          {order.invoiceId && (
            <div className="flow-toolbar" style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={() => navigate(`/invoices/${order.invoiceId}/bol`)}
                style={{ minHeight: 48, width: '100%' }}
              >
                Print BOL
              </button>
            </div>
          )}
        </div>
      )}

      {editable && (
        <div className="flow-sticky-bar">
          <button type="button" className="btn-secondary" onClick={goBack}>
            {step === 1 ? 'Back' : 'Previous'}
          </button>
          <button
            type="button"
            className="btn-danger"
            data-testid="taproom-delete-draft"
            onClick={() => void deleteDraft()}
            disabled={saving || deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
          {step < 4 ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => void goNext()}
              disabled={saving || deleting}
              data-testid="taproom-next"
            >
              {saving ? 'Saving…' : 'Next'}
            </button>
          ) : (
            <button
              type="button"
              className="btn-complete"
              data-testid="taproom-complete"
              onClick={() => void complete()}
              disabled={saving || deleting}
            >
              Complete order
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TaproomOrdersList;
